import _ from 'underscore';
import { useCallback, useReducer, useRef } from 'react';

const ACTION_TYPE_ADD_NEW_FILTER = 100;
const ACTION_TYPE_UPDATE_FILTER = 200;
const ACTION_TYPE_REMOVE_FILTER = 300;
const ACTION_TYPE_RESET_STATE = 400;
const ACTION_TYPE_REMOVE_EMPTY_FILTERS = 500;

/*
  state: [
    {
      type,   //
      title,  // e.g. 'Asset Type', 'Date added', localized values
      id,
      value,
      humanizedValue, // localized and readable values

      isFilterAlwaysVisible, // if the filter is aways visible, even its value is not set
    }
  ],
 */
/* istanbul ignore next */
const filtersReducer = (state, action) => {
  const { actionType, ...actionData } = action;
  switch (actionType) {
    case ACTION_TYPE_ADD_NEW_FILTER:
    {
      const {
        type, title, id, value, humanizedValue,
      } = actionData;

      // already exists, shouldn't happen
      if (_.find(state, { id })) {
        return state;
      }

      return [...state, {
        type, title, id, value, humanizedValue,
      }];
    }

    case ACTION_TYPE_REMOVE_FILTER:
    {
      const { id } = actionData;
      const newState = _.filter(state, filter => filter.id !== id);
      if (newState.length !== state.length) {
        return newState;
      }
      return state;
    }

    case ACTION_TYPE_RESET_STATE:
    {
      const { initialState } = actionData;
      return initialState || [];
    }

    case ACTION_TYPE_UPDATE_FILTER:
    {
      const {
        id, newValue, newHumanizedValue,
      } = actionData;
      const index = _.findIndex(state, { id });
      if (index >= 0) {
        const filterToUpdate = state[index];
        if (!_.isEqual(filterToUpdate.value, newValue) || filterToUpdate.humanizedValue !== newHumanizedValue) {
          // remove the filter
          const newState = [...state];
          if (!newHumanizedValue && filterToUpdate.isFilterAlwaysVisible !== true) {
            newState.splice(index, 1);
          } else { // replace with the new filter
            newState.splice(index, 1, { ...filterToUpdate, value: newValue, humanizedValue: newHumanizedValue });
          }
          return newState;
        }
      }
      return state;
    }

    case ACTION_TYPE_REMOVE_EMPTY_FILTERS:
    {
      const filterWithValues = _.filter(state, ({ humanizedValue, isFilterAlwaysVisible }) => humanizedValue || isFilterAlwaysVisible);
      if (filterWithValues.length !== state.length) {
        return filterWithValues;
      }
      return state;
    }

    default:
      return state;
  }
};

// this is an optional reducer to manage the filter bar state, it's not required and it's possible
// to use other ways to manage the state
/* istanbul ignore next */
export const useAdStudioFiltersReducer = ({ supportedFilterTypes, initialState, initialId = 100 }) => {
  const [filters, dispatch] = useReducer(filtersReducer, initialState);

  // use an auto increased integer number as filter id
  const idGeneratorRef = useRef(initialId);
  const getNextId = useCallback(() => {
    if (idGeneratorRef.current >= Number.MAX_SAFE_INTEGER) {
      idGeneratorRef.current = initialId;
    } else {
      idGeneratorRef.current += 1;
    }
    return idGeneratorRef.current;
  }, [initialId]);

  const removeFilter = useCallback(({ id }) => {
    dispatch({ actionType: ACTION_TYPE_REMOVE_FILTER, id });
  }, [dispatch]);

  const removeAllFilters = useCallback(() => {
    dispatch({ actionType: ACTION_TYPE_RESET_STATE, initialState });
  }, [dispatch, initialState]);

  const overwriteFilters = useCallback((state) => {
    dispatch({ actionType: ACTION_TYPE_RESET_STATE, initialState: state });
  }, [dispatch]);

  const addNewFilter = useCallback(({ type, initValue, initHumanizedValue }) => {
    const filterTypeMeta = supportedFilterTypes[type] || { type };
    const newFilter = {
      ...filterTypeMeta,
      id: getNextId(),
      value: initValue || null,
      humanizedValue: initHumanizedValue || initValue || null,
    };
    dispatch({
      actionType: ACTION_TYPE_ADD_NEW_FILTER,
      ...newFilter,
    });
    return newFilter;
  }, [dispatch, getNextId, supportedFilterTypes]);

  const updateFilter = useCallback(({ id, newValue, newHumanizedValue }) => {
    dispatch({
      actionType: ACTION_TYPE_UPDATE_FILTER,
      id,
      newValue,
      newHumanizedValue,
    });
  }, [dispatch]);

  const removeEmptyFilters = useCallback(() => {
    dispatch({ actionType: ACTION_TYPE_REMOVE_EMPTY_FILTERS });
  }, [dispatch]);

  return {
    filters,
    addNewFilter,
    overwriteFilters,
    removeFilter,
    removeAllFilters,
    removeEmptyFilters,
    updateFilter,
  };
};
